import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AutoDismissNotice,
  Button,
  EmptyState,
  StatCard,
} from "@a2z/ui";
import { imageUrl } from "@a2z/ui/media/image-url";
import { MEDIA_BASE_URL } from "@/lib/media-base";
import { ProductCreateDrawer } from "@/modules/products/components/ProductCreateDrawer";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getBuyerAbsoluteUrl, getSellerPath } from "@/lib/routing/portal-urls";
import type { CategoryView, ProductListResponse } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { formatCurrency } from "@/lib/formatting/currency";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { useProductsQuery } from "@/modules/products/services/useProductsQuery";
import { useCategoriesQuery } from "@/shared/services/useCategoriesQuery";
import { useStoreQuery } from "@/shared/services/useStoreQuery";
import { usePageChrome } from "@/shared/layout/PageChromeContext";
import {
  PageTransition,
  PageTransitionItem,
} from "@/shared/components/PageTransition";
import {
  ProductsHeroBar,
  type ProductsViewMode,
  type ProductsSort,
  type StatusFilterId,
} from "@/modules/products/components/ProductsHeroBar";
import { ProductsGridCard } from "@/modules/products/components/ProductsGridCard";
import { MobileProductsView } from "@/modules/products/components/mobile/MobileProductsView";
import { MobileProductFullScreen } from "@/modules/products/components/mobile/MobileProductFullScreen";
import { ProductsFiltersDrawer } from "@/modules/products/components/ProductsFiltersDrawer";
import { ProductActionsMenu } from "@/modules/products/components/ProductActionsMenu";
import { ProductSelectCheckbox } from "@/modules/products/components/ProductSelectCheckbox";
import { ProductsBulkCartBar } from "@/modules/products/components/ProductsBulkCartBar";
import { ProductVariantsEditorSheet } from "@/modules/products/components/ProductVariantsEditorSheet";
import { ConfirmDialog } from "@/shared/components/dialogs/ConfirmDialog";
import {
  deriveProductStatus,
  getProductCardAttention,
  getPurchasableStockTotal,
  hasUnlimitedStock,
  type ProductCardStatus,
} from "@/modules/products/lib/cardAttention";
import {
  useProductCardActions,
  type ProductCardActions,
} from "@/modules/products/lib/useProductCardActions";

function parseMoney(value: string | null): number | null {
  if (typeof value !== "string") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getProductThumbnail(
  product: ProductListResponse["items"][number],
): string | null {
  const readyImage = product.images.find((img) => img.status === "READY");
  if (!readyImage?.objectKey) return null;
  return imageUrl(
    readyImage.objectKey,
    {
      width: 240,
      format: "auto",
      removeBackground: readyImage.useBgRemoved === true,
    },
    { baseUrl: MEDIA_BASE_URL },
  );
}

type ProductsPageProps = {
  locale: Locale;
  storeSlug: string;
  categoryIds: string[];
  availability: string;
  status: string;
  search: string;
  notice: string;
  limit: number;
  offset: number;
};

export function ProductsPage({
  locale,
  storeSlug,
  categoryIds,
  availability,
  status,
  search,
  notice,
  limit,
  offset,
}: ProductsPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(
    () => searchParams.get("create") === "1",
  );
  const openCreateDrawer = () => setIsCreateDrawerOpen(true);
  const closeCreateDrawer = (state: { created: boolean }) => {
    setIsCreateDrawerOpen(false);
    // Strip ?create=1 if it's still in the URL.
    if (searchParams.get("create") === "1") {
      const next = new URLSearchParams(searchParams);
      next.delete("create");
      setSearchParams(next, { replace: true });
    }
    if (state.created) {
      // Refresh notice and ensure list shows fresh data.
      navigate(
        `${getSellerPath(locale, storeSlug)}/products?notice=product_saved`,
        {
          replace: true,
        },
      );
    }
  };
  useEffect(() => {
    localStorage.setItem("seller_pwa_engagement_v1", "true");
  }, []);

  // Interactive product actions (inline visibility toggle + delete), shared
  // across the grid, table and mobile views.
  const actions = useProductCardActions({ storeSlug });

  // Bulk selection → build a multi-product checkout link ("cart") to send a
  // buyer. In-memory only; resets on unmount.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());
  // Mobile-only selection mode: the list has no always-visible checkboxes
  // (unlike the desktop grid/table), so a header toggle switches the rows
  // into select-on-tap mode. Exiting clears the selection.
  const [selectMode, setSelectMode] = useState(false);
  const toggleSelectMode = () => {
    if (selectMode) clearSelection();
    setSelectMode(!selectMode);
  };

  // Mobile-only: tapping a row opens the full-screen product detail (tier 2).
  // URL-driven (`?product=<id>`) so the editor's back button can return to the
  // detail view via a normal navigation, and browser-back closes it.
  const openProductId = isMobile ? searchParams.get("product") : null;
  const openProductDetail = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("product", id);
    setSearchParams(next);
  };
  const closeProductDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("product");
    setSearchParams(next);
  };

  const t = useTranslations();
  const {
    data: productsData,
    isLoading: isProductsLoading,
    error: productsError,
  } = useProductsQuery({
    storeSlug,
    limit,
    offset,
    categoryIds,
    availability,
    status,
    search,
  });
  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    error: categoriesError,
  } = useCategoriesQuery({ storeSlug });
  const { data: storeData } = useStoreQuery({ storeSlug });
  const maxProductImageCount = storeData?.maxProductImageCount ?? 3;
  // Store-level low-stock threshold; a product's own override still wins inside
  // deriveProductStatus. Undefined until the store loads (falls back to default).
  const storeLowStock = storeData?.lowStockThreshold;

  const hasSearch = search.length >= 3;
  const showSaveNotice = notice === "product_saved";

  const basePath = `${getSellerPath(locale, storeSlug)}/products`;

  const clearFiltersHref = (() => {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("offset", "0");
    return `${basePath}?${params.toString()}`;
  })();

  const buildPageHref = (nextOffset: number) => {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("offset", String(nextOffset));
    if (categoryIds.length > 0) {
      categoryIds.forEach((id) => params.append("categoryIds[]", id));
    }
    if (availability) params.set("availability", availability);
    if (status) params.set("status", status);
    if (hasSearch) params.set("search", search);
    return `${basePath}?${params.toString()}`;
  };

  // View mode + sort persisted in localStorage so it survives navigation.
  const [viewMode, setViewMode] = useState<ProductsViewMode>(
    () =>
      (typeof window !== "undefined" &&
        (localStorage.getItem(
          "seller_products_view",
        ) as ProductsViewMode | null)) ||
      "grid",
  );
  useEffect(() => {
    localStorage.setItem("seller_products_view", viewMode);
  }, [viewMode]);

  const [sort, setSort] = useState<ProductsSort>("recent");

  const productsTitle = t(keys.seller.productsTitle);
  const addProductLabel = t(keys.seller.addProductAction);

  // Hero (kicker + title + count + buttons)
  const { setHero, setHeroAux } = usePageChrome();
  // Stabilize references — without these, every render produces a new {} / []
  // when the queries are still loading, which would cascade through the
  // statusCounts memo and the setHeroAux useEffect → infinite re-render.
  const items = productsData?.items;
  const total = productsData?.total;
  const data = useMemo(
    () => ({ items: items ?? [], total: total ?? 0 }),
    [items, total],
  );
  // On mobile the list is fetched inside MobileProductsView; it reports its
  // loaded page up so the shared overlays (inline stock editor, bulk cart bar)
  // can resolve selected product objects. Desktop uses the page's own query.
  const [mobileItems, setMobileItems] = useState<ProductListResponse["items"]>([]);
  const productPool = isMobile ? mobileItems : data.items;
  // Resolve the full product for the inline stock editor (opened from a card menu).
  const stockEditProduct =
    actions.stockTargetId !== null
      ? (productPool.find((p) => p.id === actions.stockTargetId) ?? null)
      : null;
  const categories = useMemo(() => categoriesData ?? [], [categoriesData]);
  // Stable string key for categoryIds — array prop reference can change between
  // route renders even when contents are equal.
  const categoryIdsKey = categoryIds.join(",");

  // The page renders its own inline header (like coupons/customers) so the top
  // sits in the same body flow on the canvas — no separate hero band.
  useEffect(() => {
    setHero(null);
    return () => setHero(null);
  }, [setHero]);

  const todayLabel = new Intl.DateTimeFormat(
    locale === "ar" ? "ar-EG" : "en-US",
    { day: "numeric", month: "long", year: "numeric" },
  ).format(new Date());
  const desktopHeader = (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-[24px] font-bold leading-tight tracking-[-0.01em] text-[color:var(--text-primary)]">
          {productsTitle}
        </h1>
        <p className="mt-[5px] text-[13.5px] text-[color:var(--text-muted)]">
          {t(keys.products.pageSubtitle)}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2.5">
        <span className="inline-flex items-center gap-2 rounded-[11px] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-[13px] py-[9px] text-[13px] font-semibold text-[color:var(--text-secondary)]">
          <CalendarIcon />
          {todayLabel}
        </span>
        <Button
          type="button"
          onClick={openCreateDrawer}
          variant="secondary"
          size="md"
          leadingIcon={<PlusIcon />}
        >
          {addProductLabel}
        </Button>
      </div>
    </div>
  );

  // Hero aux (filter bar)
  const statusCounts = useMemo<Record<StatusFilterId, number>>(() => {
    const items = data.items;
    return {
      all: data.total,
      active: items.filter((p) => deriveProductStatus(p, storeLowStock) === "active").length,
      low_stock: items.filter((p) => deriveProductStatus(p, storeLowStock) === "low_stock")
        .length,
      out_of_stock: items.filter(
        (p) => deriveProductStatus(p, storeLowStock) === "out_of_stock",
      ).length,
      hidden: items.filter((p) => deriveProductStatus(p, storeLowStock) === "hidden").length,
    };
  }, [data.items, data.total]);

  const currentStatus = (status as StatusFilterId) || "all";

  // Active category/sort filters — badge on the Filter & sort button (desktop
  // hero + mobile header). Defined before the hero effect that consumes it.
  const activeFilterCount =
    (categoryIds.length > 0 ? 1 : 0) + (sort !== "recent" ? 1 : 0);

  // The desktop toolbar (status tabs + filter + view toggle) now lives inside
  // the wrapper card in the body, so the shared hero-aux slot stays empty.
  useEffect(() => {
    setHeroAux(null);
    return () => setHeroAux(null);
  }, [setHeroAux]);
  // Referenced by the mobile header + the inline toolbar below; kept to avoid
  // unused-var churn while the categoryIds string key stays the stable dep.
  void categoryIdsKey;

  // Local sort applied client-side over the current page.
  // NOTE: must run BEFORE any early returns to keep hook order stable.
  const sortedItems = useMemo(() => {
    const copy = [...data.items];
    const priceOf = (p: ProductListResponse["items"][number]) => {
      const v =
        p.variants.find((x) => x.id === p.defaultVariantId) ??
        p.variants.find((x) => x.isDefault) ??
        p.variants[0];
      return v ? (v.discountedPrice ?? v.price) : 0;
    };
    const stockOf = (p: ProductListResponse["items"][number]) =>
      getPurchasableStockTotal(p);
    switch (sort) {
      case "price-asc":
        return copy.sort((a, b) => priceOf(a) - priceOf(b));
      case "price-desc":
        return copy.sort((a, b) => priceOf(b) - priceOf(a));
      case "stock-asc":
        return copy.sort((a, b) => stockOf(a) - stockOf(b));
      case "stock-desc":
        return copy.sort((a, b) => stockOf(b) - stockOf(a));
      case "sales":
      case "recent":
      default:
        return copy;
    }
  }, [data.items, sort]);

  if (
    (isProductsLoading && !productsData) ||
    (isCategoriesLoading && !categoriesData)
  ) {
    return (
      <section className="px-7 py-5">
        <div className="grid gap-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-xl bg-[color:var(--surface-alt)]"
            />
          ))}
        </div>
      </section>
    );
  }

  if (productsError || categoriesError) {
    return (
      <section className="px-7 py-5">
        <div className="rounded-xl border border-[color:var(--error-100)] bg-[color:var(--error-50,var(--surface))] px-4 py-4 text-sm text-[color:var(--error-500)]">
          {t(keys.errors.generic)}
        </div>
      </section>
    );
  }

  const hasPrevious = offset > 0;
  const hasNext = offset + limit < data.total;
  const hasFilters = Boolean(
    categoryIds.length > 0 || availability || status || hasSearch,
  );

  const pageCount = Math.max(1, Math.ceil(data.total / limit));
  const currentPage = Math.min(pageCount, Math.floor(offset / limit) + 1);
  const shownRange =
    data.total > 0
      ? `${offset + 1}–${Math.min(offset + limit, data.total)}`
      : "0";

  const statsRow = (
    <div className="grid grid-cols-2 gap-3.5 sm:[grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
      <StatCard
        icon={<BoxIcon />}
        label={productsTitle}
        value={data.total}
      />
      <StatCard
        icon={<CheckIcon />}
        label={t(keys.products.statusActive)}
        value={statusCounts.active}
      />
      <StatCard
        icon={<AlertIcon />}
        label={t(keys.products.statusLowStock)}
        value={statusCounts.low_stock}
      />
      <StatCard
        className="col-span-2 sm:col-span-1"
        icon={<SlashIcon />}
        label={t(keys.products.statusOutOfStock)}
        value={statusCounts.out_of_stock}
      />
    </div>
  );

  const contentBody =
    data.items.length === 0 ? (
      hasFilters ? (
        <EmptyState
          title={t(keys.products.filteredEmptyTitle)}
          description={t(keys.products.filteredEmptyHint)}
          actionLabel={t(keys.common.clearFilters)}
          actionHref={clearFiltersHref}
          actionVariant="outline"
        />
      ) : (
        <EmptyState
          title={t(keys.common.emptyTitle)}
          description={t(keys.seller.emptyProductsHint)}
          actionLabel={t(keys.seller.addProductAction)}
          onAction={openCreateDrawer}
        />
      )
    ) : viewMode === "grid" ? (
      <ProductsGridView
        products={sortedItems}
        storeLowStock={storeLowStock}
        categories={categories}
        locale={locale}
        storeSlug={storeSlug}
        basePath={basePath}
        actions={actions}
        selectMode={selectMode}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
      />
    ) : (
      <ProductsTableView
        products={sortedItems}
        storeLowStock={storeLowStock}
        categories={categories}
        locale={locale}
        storeSlug={storeSlug}
        basePath={basePath}
        actions={actions}
        selectMode={selectMode}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
      />
    );

  const desktopFooter = data.items.length > 0 ? (
    <>
      <div
        aria-hidden="true"
        className="my-[18px] h-4"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg,var(--border-strong) 0 1px,transparent 1px 9px)",
          WebkitMaskImage:
            "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)",
          maskImage:
            "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)",
        }}
      />
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13px] text-[color:var(--text-muted)]">
          {t(keys.products.paginationShowing)} {shownRange} / {data.total}
        </span>
        {pageCount > 1 ? (
          <div className="flex items-center gap-1">
            <PagerButton
              ariaLabel={t(keys.common.previous)}
              disabled={!hasPrevious}
              onClick={() => navigate(buildPageHref(Math.max(offset - limit, 0)))}
            >
              <ChevronLeftIcon />
            </PagerButton>
            {pageWindow(currentPage, pageCount).map((item, i) =>
              typeof item === "number" ? (
                <button
                  key={item}
                  type="button"
                  onClick={() => navigate(buildPageHref((item - 1) * limit))}
                  aria-current={item === currentPage ? "page" : undefined}
                  className={[
                    "h-[34px] min-w-[34px] rounded-[9px] px-2 text-[13px] font-semibold transition-colors",
                    item === currentPage
                      ? "bg-[color:var(--accent-500)] text-[color:var(--on-accent)]"
                      : "border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-secondary)] hover:bg-[color:var(--hover)]",
                  ].join(" ")}
                >
                  {item}
                </button>
              ) : (
                <span
                  key={`ellipsis-${i}`}
                  className="px-1 text-[13px] text-[color:var(--text-muted)]"
                >
                  …
                </span>
              ),
            )}
            <PagerButton
              ariaLabel={t(keys.common.next)}
              disabled={!hasNext}
              onClick={() => navigate(buildPageHref(offset + limit))}
            >
              <ChevronRightIcon />
            </PagerButton>
          </div>
        ) : null}
      </div>
    </>
  ) : null;

  return (
    <>
      {isMobile ? (
        // Mobile Products renders full-bleed OUTSIDE PageTransition — the page
        // transition uses a transform, which would break the sticky header.
        <div className="flex flex-col px-4 pb-8">
          <MobileProductsView
            storeSlug={storeSlug}
            locale={locale}
            storeLowStock={storeLowStock}
            onOpenProduct={openProductDetail}
            onNewProduct={openCreateDrawer}
            selectMode={selectMode}
            onToggleSelectMode={toggleSelectMode}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onLoadedItemsChange={setMobileItems}
          />
        </div>
      ) : (
        <PageTransition className="w-full">
          <div className="flex flex-col gap-5 px-7 py-5">
            {showSaveNotice ? (
              <PageTransitionItem>
                <AutoDismissNotice
                  title={t(keys.seller.productSavedShare)}
                  closeLabel={t(keys.common.close)}
                />
              </PageTransitionItem>
            ) : null}

            <PageTransitionItem>{desktopHeader}</PageTransitionItem>
            <PageTransitionItem>{statsRow}</PageTransitionItem>
            <PageTransitionItem>
              <div className="rounded-[20px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4">
                <div className="mb-4">
                  <ProductsHeroBar
                    basePath={basePath}
                    limit={limit}
                    selectedCategoryIds={categoryIds}
                    status={currentStatus}
                    search={search}
                    statusCounts={statusCounts}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    activeFilterCount={activeFilterCount}
                    onOpenFilters={() => setIsFiltersDrawerOpen(true)}
                    trailing={
                      <button
                        type="button"
                        onClick={toggleSelectMode}
                        aria-pressed={selectMode}
                        className={[
                          "inline-flex h-[34px] shrink-0 items-center gap-2 rounded-[10px] border px-3.5 text-[12.5px] font-semibold transition-colors",
                          selectMode
                            ? "border-[color:var(--accent-500)] bg-[color:var(--accent-500)] text-[color:var(--on-accent)]"
                            : "border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-secondary)] hover:bg-[color:var(--hover)]",
                        ].join(" ")}
                      >
                        <SelectIcon />
                        {selectMode
                          ? t(keys.products.bulkSelectExit)
                          : t(keys.products.bulkSelectToggle)}
                      </button>
                    }
                  />
                </div>
                {contentBody}
                {desktopFooter}
              </div>
            </PageTransitionItem>
          </div>
        </PageTransition>
      )}

      {/* Overlays + FAB rendered as siblings of PageTransition. The
        motion.section transform creates a containing block that would
        otherwise re-parent `position: fixed` descendants away from the
        viewport. */}
      {isCreateDrawerOpen ? (
        <ProductCreateDrawer
          locale={locale}
          storeSlug={storeSlug}
          categories={categories}
          maxProductImageCount={maxProductImageCount}
          onClose={closeCreateDrawer}
        />
      ) : null}

      <ProductsFiltersDrawer
        open={isFiltersDrawerOpen}
        onClose={() => setIsFiltersDrawerOpen(false)}
        basePath={basePath}
        limit={limit}
        locale={locale}
        categories={categories}
        selectedCategoryIds={categoryIds}
        sort={sort}
        onSortChange={setSort}
      />

      <ConfirmDialog
        isOpen={actions.deleteTarget !== null}
        title={t(keys.products.deleteConfirmTitle)}
        body={t(keys.products.deleteConfirmBody)}
        confirmLabel={t(keys.common.delete)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={actions.isDeleting}
        onConfirm={actions.confirmDelete}
        onCancel={actions.cancelDelete}
      />

      {/* Inline stock editor — opened from any card's ⋯ menu, no redirect. */}
      {stockEditProduct ? (
        <ProductVariantsEditorSheet
          isOpen
          mode="stock"
          onClose={actions.closeEditStock}
          storeSlug={storeSlug}
          product={stockEditProduct}
        />
      ) : null}

      <ProductsBulkCartBar
        storeSlug={storeSlug}
        selectedProducts={productPool.filter((p) => selectedIds.has(p.id))}
        onClear={clearSelection}
      />

      {isMobile ? (
        <MobileProductFullScreen
          storeSlug={storeSlug}
          productId={openProductId}
          basePath={basePath}
          locale={locale}
          onClose={closeProductDetail}
          actions={actions}
        />
      ) : null}
    </>
  );
}

// ─── Grid view ───────────────────────────────────────────────────────────────

function ProductsGridView({
  products,
  categories,
  locale,
  storeSlug,
  basePath,
  actions,
  selectMode,
  selectedIds,
  onToggleSelect,
  storeLowStock,
}: {
  products: ProductListResponse["items"];
  categories: CategoryView[];
  locale: Locale;
  storeSlug: string;
  basePath: string;
  actions: ProductCardActions;
  selectMode: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  storeLowStock?: number;
}) {
  const t = useTranslations();
  return (
    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {products.map((product) => {
        const productStatus = deriveProductStatus(product, storeLowStock);
        const thumbnail = getProductThumbnail(product);
        const primaryCategory =
          product.categoryIds
            .map((id) => categories.find((c) => c.id === id))
            .filter(Boolean)
            .map((c) =>
              pickLocalized(
                locale,
                (c as CategoryView).nameAr,
                (c as CategoryView).nameEn,
              ),
            )[0] ?? null;
        const defaultVariant =
          product.variants.find((v) => v.id === product.defaultVariantId) ??
          product.variants.find((v) => v.isDefault) ??
          product.variants[0];
        const fallbackCurrent = defaultVariant
          ? (defaultVariant.discountedPrice ?? defaultVariant.price)
          : 0;
        const fallbackOriginal = defaultVariant ? defaultVariant.price : null;
        const currentMin =
          parseMoney(product.pricingSummary.currentMin) ?? fallbackCurrent;
        const originalMin =
          parseMoney(product.pricingSummary.originalMin) ?? fallbackOriginal;
        const hasDiscount = originalMin !== null && originalMin > currentMin;
        const discountPct =
          hasDiscount && originalMin
            ? Math.round((1 - currentMin / originalMin) * 100)
            : null;
        const stockTotal = getPurchasableStockTotal(product);
        const editHref = `${basePath}/${product.id}${product.images.length === 0 ? "#photos" : ""}`;
        // Buyer route parses `<slug>-<uuid36>`; passing just `slug` 404s.
        const sharePath = getBuyerAbsoluteUrl(
          locale,
          storeSlug,
          `/products/${encodeURIComponent(product.productRef)}`,
        );
        return (
          <ProductsGridCard
            key={product.id}
            product={product}
            status={productStatus}
            category={primaryCategory}
            thumbnail={thumbnail}
            currentPrice={formatCurrency(currentMin, locale)}
            originalPrice={
              hasDiscount && originalMin
                ? formatCurrency(originalMin, locale)
                : null
            }
            discountPct={discountPct}
            stockTotal={stockTotal}
            isUnlimitedStock={hasUnlimitedStock(product)}
            editHref={editHref}
            shareHref={sharePath}
            sold30dLabel={null}
            pendingVisibility={actions.pendingVisibilityId === product.id}
            onToggleVisibility={() =>
              actions.toggleVisibility({
                id: product.id,
                name: pickLocalized(locale, product.nameAr, product.nameEn),
                isActive: product.isActive,
              })
            }
            onRequestDelete={() =>
              actions.requestDelete({
                id: product.id,
                name: pickLocalized(locale, product.nameAr, product.nameEn),
                isActive: product.isActive,
              })
            }
            onEditStock={() => actions.requestEditStock(product.id)}
            onAddPhoto={(file) => actions.addPhoto(product.id, file)}
            pendingPhoto={actions.pendingPhotoId === product.id}
            selectMode={selectMode}
            selected={selectedIds.has(product.id)}
            onToggleSelect={() => onToggleSelect(product.id)}
            selectAriaLabel={t(keys.products.bulkSelectAria)}
          />
        );
      })}
    </div>
  );
}

// ─── Table view ──────────────────────────────────────────────────────────────

type Row = ProductListResponse["items"][number];

const TABLE_COLS =
  "grid-cols-[minmax(0,2.5fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.9fr)]";

const TABLE_STATUS_TONE: Record<
  ProductCardStatus,
  { bg: string; fg: string; dot: string }
> = {
  active: {
    bg: "color-mix(in oklab, #16A34A 12%, transparent)",
    fg: "var(--success-500)",
    dot: "#16A34A",
  },
  low_stock: {
    bg: "color-mix(in oklab, #D97706 12%, transparent)",
    fg: "var(--warning-500)",
    dot: "#D97706",
  },
  out_of_stock: {
    bg: "color-mix(in oklab, #E5484D 12%, transparent)",
    fg: "var(--error-500)",
    dot: "#E5484D",
  },
  hidden: {
    bg: "var(--surface-alt)",
    fg: "var(--text-muted)",
    dot: "var(--text-disabled)",
  },
};

const TABLE_STATUS_SHORT: Record<ProductCardStatus, string> = {
  active: keys.products.statusActive,
  low_stock: keys.products.statusLowStock,
  out_of_stock: keys.products.statusOutOfStock,
  hidden: keys.products.statusHiddenShort,
};

function ProductsTableView({
  products,
  categories,
  locale,
  storeSlug,
  basePath,
  actions,
  selectMode,
  selectedIds,
  onToggleSelect,
  storeLowStock,
}: {
  products: Row[];
  categories: CategoryView[];
  locale: Locale;
  storeSlug: string;
  basePath: string;
  actions: ProductCardActions;
  selectMode: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  storeLowStock?: number;
}) {
  const t = useTranslations();
  const navigate = useNavigate();

  const editHrefOf = (p: Row) =>
    `${basePath}/${p.id}${p.images.length === 0 ? "#photos" : ""}`;

  return (
    <div className="overflow-hidden rounded-[16px] border border-[color:var(--border-light)] bg-[color:var(--surface)]">
      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          {/* header */}
          <div
            className={`grid ${TABLE_COLS} border-b border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-5 py-[13px] text-[12px] font-semibold text-[color:var(--text-muted)]`}
          >
            <span>{t(keys.products.tableColProduct)}</span>
            <span>{t(keys.products.categories)}</span>
            <span>{t(keys.common.price)}</span>
            <span>{t(keys.products.stockLabel)}</span>
            <span>{t(keys.common.status)}</span>
            <span className="text-end">{t(keys.products.moreActions)}</span>
          </div>

          {products.map((p) => {
            const productStatus = deriveProductStatus(p, storeLowStock);
            const attention = getProductCardAttention(
              {
                status: productStatus,
                hiddenReasons: p.sellerBuyerHiddenReasons ?? [],
                stockTotal: getPurchasableStockTotal(p),
              },
              t,
            );
            const rowTint = attention
              ? attention.tone === "error"
                ? "bg-[color:color-mix(in_oklab,var(--error-500)_7%,transparent)]"
                : "bg-[color:color-mix(in_oklab,var(--gold-500)_10%,transparent)]"
              : "";
            const thumb = getProductThumbnail(p);
            const name = pickLocalized(locale, p.nameAr, p.nameEn);
            const category =
              p.categoryIds
                .map((id) => categories.find((c) => c.id === id))
                .filter(Boolean)
                .map((c) =>
                  pickLocalized(
                    locale,
                    (c as CategoryView).nameAr,
                    (c as CategoryView).nameEn,
                  ),
                )[0] ?? null;
            const v =
              p.variants.find((x) => x.id === p.defaultVariantId) ??
              p.variants.find((x) => x.isDefault) ??
              p.variants[0];
            const current = v ? (v.discountedPrice ?? v.price) : 0;
            const original = v ? v.price : null;
            const hasDiscount = original !== null && original > current;
            const stockTotal = getPurchasableStockTotal(p);
            const stockText = hasUnlimitedStock(p)
              ? t(keys.products.editSidebarInventoryUnlimited)
              : stockTotal === 0
                ? t(keys.products.statusOutOfStock)
                : t(keys.products.mobileInStock, { count: stockTotal });
            const selected = selectedIds.has(p.id);
            const tone = TABLE_STATUS_TONE[productStatus];
            const editHref = editHrefOf(p);
            const shareHref = getBuyerAbsoluteUrl(
              locale,
              storeSlug,
              `/products/${encodeURIComponent(p.productRef)}`,
            );
            const activate = () => {
              if (selectMode) onToggleSelect(p.id);
              else navigate(editHref);
            };
            return (
              <div
                key={p.id}
                onClick={activate}
                className={`grid ${TABLE_COLS} cursor-pointer items-center border-b border-[color:var(--hair)] px-5 py-[11px] text-[13px] transition-colors last:border-b-0 hover:bg-[color:var(--hover)] ${rowTint}`}
              >
                {/* product */}
                <span className="flex min-w-0 items-center gap-[11px] font-semibold">
                  {selectMode || selected ? (
                    <span onClick={(e) => e.stopPropagation()}>
                      <ProductSelectCheckbox
                        checked={selected}
                        onToggle={() => onToggleSelect(p.id)}
                        ariaLabel={t(keys.products.bulkSelectAria)}
                      />
                    </span>
                  ) : null}
                  <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-[color:var(--surface-alt)] text-[color:var(--text-secondary)]">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt=""
                        aria-hidden="true"
                        className={`h-full w-full object-cover ${productStatus === "hidden" ? "opacity-45" : ""}`}
                      />
                    ) : (
                      <TableBagIcon />
                    )}
                  </span>
                  <span className="truncate text-[color:var(--text-primary)]">
                    {name}
                  </span>
                </span>
                {/* category */}
                <span className="truncate text-[color:var(--text-secondary)]">
                  {category ?? "—"}
                </span>
                {/* price */}
                <span className="flex min-w-0 items-baseline gap-1.5">
                  <span className="font-semibold text-[color:var(--text-primary)]">
                    <bdi>{formatCurrency(current, locale)}</bdi>
                  </span>
                  {hasDiscount && original ? (
                    <span className="shrink-0 text-[11.5px] text-[color:var(--text-muted)] line-through">
                      <bdi>{formatCurrency(original, locale)}</bdi>
                    </span>
                  ) : null}
                </span>
                {/* stock */}
                <span className="truncate text-[color:var(--text-secondary)]">
                  {stockText}
                </span>
                {/* status */}
                <span>
                  <span
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-[9px] py-[3px] text-[11.5px] font-semibold"
                    style={{ background: tone.bg, color: tone.fg }}
                  >
                    <span
                      aria-hidden="true"
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: tone.dot }}
                    />
                    {t(TABLE_STATUS_SHORT[productStatus])}
                  </span>
                </span>
                {/* actions */}
                <span
                  className="flex items-center justify-end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ProductActionsMenu
                    target={{ id: p.id, name, isActive: p.isActive }}
                    editHref={editHref}
                    previewHref={shareHref}
                    pendingVisibility={actions.pendingVisibilityId === p.id}
                    onToggleVisibility={() =>
                      actions.toggleVisibility({
                        id: p.id,
                        name,
                        isActive: p.isActive,
                      })
                    }
                    onRequestDelete={() =>
                      actions.requestDelete({
                        id: p.id,
                        name,
                        isActive: p.isActive,
                      })
                    }
                    onEditStock={() => actions.requestEditStock(p.id)}
                    triggerClassName="!h-8 !w-8 !rounded-[9px] !text-[color:var(--text-muted)]"
                  />
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TableBagIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 8V6a4 4 0 0 1 8 0v2" />
      <rect x="5" y="8" width="14" height="13" rx="2.5" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2.5" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

/** Page numbers to render: all when ≤7, else windowed (first, last, current ±1) with "…". */
function pageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const wanted = [1, total, current, current - 1, current + 1]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  let prev = 0;
  for (const p of [...new Set(wanted)]) {
    if (p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

function PagerButton({
  ariaLabel,
  disabled,
  onClick,
  children,
}: {
  ariaLabel: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)] disabled:cursor-not-allowed disabled:text-[color:var(--text-disabled)] disabled:hover:bg-[color:var(--surface)]"
    >
      {children}
    </button>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}
function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}
function BoxIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="m3 8 9 5 9-5" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}
function SlashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="m5.6 5.6 12.8 12.8" />
    </svg>
  );
}
function SelectIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}
